import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import classNames from 'classnames';
import { z } from 'zod';

import { ConnectWalletGuard, useTokenBalanceByName } from '@shared/helpers/web3/wallet';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMinBetAmount } from '@store:web/components/pages/MagicPredictionsPage/api/useMinBetAmount/useMinBetAmount';
import { usePredictionBet } from '@store:web/components/pages/MagicPredictionsPage/api/usePredictionBet/usePredictionBet';
import type { PredictionRoundParticipation } from '@store:web/components/pages/MagicPredictionsPage/model/AdjacentRound/roundParticipation';
import { FormAmountInputWithPresets } from '@ui/uikit/components/common/FormAmountInputWithPresets';
import { FormControl } from '@ui/uikit/components/common/FormControl';
import { formatReward } from '@ui/uikit/utils';

import type { AdjacentRound } from '../../../../../../../model/AdjacentRound/adjacentRound';
import { PredictionDirection } from '../../../../../../../model/PredictionDirection';
import { PredictionOption } from '../../PredictionOption/PredictionOption';
import { MakePredictionSubmitButton } from './MakePredictionSubmitButton';

type MakePredictionFormProps = {
  onSubmit: () => void;
  defaultDirection: PredictionDirection;
  round: AdjacentRound;
  participation: PredictionRoundParticipation;
};

const DIRECTIONS = [PredictionDirection.UP, PredictionDirection.DOWN] as const;

const renderHintMessage = (
  balance?: number,
  minAvailableBetValue?: number,
  errorMessage?: string,
) => {
  return (
    <div>
      {Boolean(errorMessage) && <div>{errorMessage}</div>}
      {minAvailableBetValue !== 0 && (
        <ConnectWalletGuard fallback="Connect Wallet">
          Wallet Balance: {formatReward(balance ?? 0)} USDT
        </ConnectWalletGuard>
      )}
    </div>
  );
};

export const MakePredictionForm = ({
  defaultDirection,
  round,
  onSubmit,
  participation,
}: MakePredictionFormProps) => {
  const [defaultValues] = useState(() => {
    return { amount: '0', direction: defaultDirection };
  });

  const { data: tokenBalance, isPending: isTokenBalanceLoading } = useTokenBalanceByName({
    tokenName: 'USDT',
  });
  const walletBalance = tokenBalance?.value ?? 0;

  const { data: minBetAmount } = useMinBetAmount();
  const minAvailableBetValue = minBetAmount?.value ?? 0;
  const minAvailableBetFormattedValue = minBetAmount?.formatted;

  const FormSchema = useMemo(() => {
    return z.object({
      amount: z.string().refine(
        (value) => {
          const numberValue = parseFloat(value);
          return numberValue >= minAvailableBetValue;
        },
        {
          message:
            minAvailableBetValue === 0
              ? 'Something went wrong. Try again later.'
              : `Amount must be at least ${minAvailableBetFormattedValue}`,
        },
      ),
      direction: z.enum(DIRECTIONS),
    });
  }, [minAvailableBetFormattedValue, minAvailableBetValue]);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues,
  });

  const { handleSubmit, control, watch, formState, trigger } = form;

  const watchedAmount = watch('amount');
  const watchedDirection = watch('direction');

  const { isDirty, errors, isSubmitting } = formState;
  const hasErrors = Object.keys(errors).length > 0;
  const isSubmitButtonDisabled =
    !isDirty ||
    hasErrors ||
    isSubmitting ||
    !watchedAmount ||
    isTokenBalanceLoading ||
    minAvailableBetValue === 0;

  const maxBetAmount = !isTokenBalanceLoading && tokenBalance ? +tokenBalance.value.toFixed(2) : 0;
  const { betPrediction } = usePredictionBet();

  const submit = handleSubmit(async () => {
    const result = await betPrediction({
      amount: Number(watchedAmount),
      direction: watchedDirection,
    });

    if (result) {
      onSubmit();
    }
  });

  useEffect(() => {
    if (!isTokenBalanceLoading && walletBalance < minAvailableBetValue) {
      void trigger('amount');
    }
  }, [isTokenBalanceLoading, minAvailableBetValue, trigger, walletBalance]);

  return (
    <>
      <form onSubmit={submit} className={classNames('flex flex-col pt-6 max-md-msq:pt-4')}>
        <div className="flex flex-col gap-6 max-md-msq:gap-4">
          <FormControl label="Your Prediction">
            <Controller
              control={control}
              name="direction"
              render={({ field: { onChange, value } }) => {
                return (
                  <div className="flex gap-2">
                    {DIRECTIONS.map((direction) => {
                      const isOptionDisabled =
                        participation.isParticipated && direction !== participation.direction;

                      const selectedOrDefaultState = value === direction ? 'selected' : 'default';

                      const optionState = isOptionDisabled ? 'disabled' : selectedOrDefaultState;

                      return (
                        <PredictionOption
                          key={direction}
                          onClick={onChange}
                          direction={direction}
                          value={
                            direction === PredictionDirection.UP
                              ? round.bullAmount.formatted
                              : round.bearAmount.formatted
                          }
                          state={optionState}
                        />
                      );
                    })}
                  </div>
                );
              }}
            />
          </FormControl>
          <FormAmountInputWithPresets
            name="amount"
            label="USDT Amount"
            control={control}
            min={0}
            max={maxBetAmount}
            showPlusMinus={false}
            decimals={2}
            placeholder={String(minAvailableBetValue) ?? ''}
            presets={{
              values: [10, 25, 50, 75],
              variant: 'percents',
            }}
            renderHintMessage={(errorMessage) =>
              renderHintMessage(tokenBalance?.value, minAvailableBetValue, errorMessage)
            }
          />
        </div>
        <MakePredictionSubmitButton
          isDisabled={isSubmitButtonDisabled}
          walletBalance={walletBalance}
        />
      </form>
    </>
  );
};
